import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Play, Pause, Headphones, Lock, Loader2, Rewind, FastForward } from "lucide-react";
import { toast } from "sonner";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const PROGRESS_PREFIX = "audio_progress_v1";
const FINISHED_MARGIN_SECONDS = 5; // treat "finished" if within N seconds of the end

// Format seconds → "m:ss"
function fmt(seconds) {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Persisted progress helpers (localStorage, per-device)
function progressKey(translation, book, chapter) {
  return `${PROGRESS_PREFIX}:${translation}:${book}:${chapter}`;
}
function loadProgress(translation, book, chapter) {
  try {
    const raw = localStorage.getItem(progressKey(translation, book, chapter));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.position !== "number") return null;
    return parsed; // { position, duration, updated_at }
  } catch (_e) {
    return null;
  }
}
function saveProgress(translation, book, chapter, position, duration) {
  try {
    localStorage.setItem(
      progressKey(translation, book, chapter),
      JSON.stringify({ position, duration, updated_at: Date.now() })
    );
  } catch (_e) {
    // storage full or disabled — ignore
  }
}
function clearProgress(translation, book, chapter) {
  try {
    localStorage.removeItem(progressKey(translation, book, chapter));
  } catch (_e) {
    // ignore
  }
}

export default function AudioBiblePlayer({ translation, book, chapter, lang }) {
  const { user } = useApp();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const lastSaveRef = useRef(0);
  const resumePendingRef = useRef(null); // seconds to seek to after metadata loads
  const [status, setStatus] = useState("idle"); // idle | loading | ready | playing | paused | error
  const [audioSrc, setAudioSrc] = useState(null);
  const [voice, setVoice] = useState(null);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [cacheHit, setCacheHit] = useState(null); // null | true | false
  const [dramatized, setDramatized] = useState(false);
  const [provider, setProvider] = useState(null); // 'elevenlabs' | 'openai'
  const [native, setNative] = useState(false);
  const [longChapter, setLongChapter] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null); // { position, duration }

  const isSupporter = !!user?.is_supporter;
  const T = (en, es) => (lang === "es" ? es : en);

  // Reset when chapter/translation changes + load any saved progress for the idle chip
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setStatus("idle");
    setAudioSrc(null);
    setVoice(null);
    setCurrent(0);
    setDuration(0);
    setCacheHit(null);
    lastSaveRef.current = 0;
    resumePendingRef.current = null;
    // Read saved position (for the "Continue from X:XX" hint)
    const p = loadProgress(translation, book, chapter);
    if (p && p.position > 3 && (!p.duration || p.position < p.duration - FINISHED_MARGIN_SECONDS)) {
      setSavedProgress(p);
      resumePendingRef.current = p.position;
    } else {
      setSavedProgress(null);
    }
  }, [translation, book, chapter]);

  // Save current position on tab close / navigate away
  useEffect(() => {
    const handler = () => {
      const a = audioRef.current;
      if (a && a.currentTime > 0 && a.duration) {
        if (a.currentTime >= a.duration - FINISHED_MARGIN_SECONDS) {
          clearProgress(translation, book, chapter);
        } else {
          saveProgress(translation, book, chapter, a.currentTime, a.duration);
        }
      }
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      handler(); // save on unmount too (e.g. route change)
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, [translation, book, chapter]);

  const generate = async (attempt = 1) => {
    if (status === "loading" && attempt === 1) return;
    setStatus("loading");
    setLongChapter(attempt > 1);
    try {
      const res = await api.post("/bible/audio/chapter", { translation, book, chapter });
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${backend}${res.data.url}`;
      setAudioSrc(url);
      setVoice(res.data.voice);
      setDramatized(!!res.data.dramatized);
      setProvider(res.data.provider || null);
      setNative(!!res.data.native);
      setCacheHit(!!res.data.cache_hit);
      setLongChapter(false);
      // If Spanish fell back to OpenAI (e.g., ElevenLabs quota exhausted), inform user politely.
      if (res.data.fallback_reason === "quota_exceeded") {
        toast.message(T("Using backup voice this month (native voice quota reached).", "Usando voz de respaldo este mes (cuota de voz nativa alcanzada)."));
      }
      // Autoplay once loaded
      setStatus("ready");
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Cloudflare caps at ~100s for a single HTTP call. For truly long chapters the
      // backend keeps running and caches the audio; retry only on gateway TIMEOUTS,
      // not on 5xx with a known business detail. Also retry when there's no response
      // at all (network drop / Cloudflare 524).
      const isTimeout = code === 504 || code === 524 || !code;
      if (isTimeout && attempt < 4) {
        setLongChapter(true);
        const waitMs = attempt * 15000; // 15s, 30s, 45s
        await new Promise((r) => setTimeout(r, waitMs));
        return generate(attempt + 1);
      }
      if (detail === "supporter_only") {
        toast.error(T("Supporters only", "Solo para sostenedores"));
      } else if (detail === "quota_exceeded") {
        toast.error(T("Monthly voice quota reached. Try again next month.", "Cuota mensual de voz alcanzada. Intenta el próximo mes."));
      } else if (detail === "tts_failed") {
        toast.error(T("Audio generation failed. Please try again.", "Falló la generación. Intenta de nuevo."));
      } else if (detail === "bible_source_error") {
        toast.error(T("Bible source unavailable. Please try again.", "Fuente de Biblia no disponible. Intenta de nuevo."));
      } else {
        toast.error(T("Something went wrong. Try again.", "Algo salió mal. Intenta de nuevo."));
      }
      setStatus("error");
    }
  };

  // Autoplay + resume seek happen together inside <audio> handlers (see onCanPlay below)
  // so we intentionally do NOT call play() from a separate effect here.

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  };

  const seek = (deltaSeconds) => {
    if (!audioRef.current) return;
    const next = Math.max(0, Math.min(duration || 0, audioRef.current.currentTime + deltaSeconds));
    audioRef.current.currentTime = next;
  };

  const changeSpeed = () => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const onScrub = (e) => {
    if (!audioRef.current || !duration) return;
    const pct = Number(e.target.value) / 100;
    audioRef.current.currentTime = duration * pct;
  };

  // Locked state for non-supporters
  if (!isSupporter) {
    return (
      <div
        className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 flex items-center gap-4"
        data-testid="audio-locked"
      >
        <div className="w-10 h-10 rounded-full bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
          <Lock className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
            {T("Audio Bible · Supporters", "Biblia en audio · Sostenedores")}
          </p>
          <p className="text-sm text-[var(--text-primary)] mt-0.5 font-serif">
            {T("Listen to this chapter with a natural narrator", "Escucha este capítulo con un narrador natural")}
          </p>
        </div>
        <button
          onClick={() => navigate("/support")}
          data-testid="audio-unlock-btn"
          className="px-4 py-2 rounded-full bg-[var(--accent)] text-white text-xs uppercase tracking-widest hover:bg-[var(--accent)]/90 transition-colors flex-shrink-0"
        >
          {T("Unlock", "Desbloquear")}
        </button>
      </div>
    );
  }

  // Idle state (supporter, hasn't tapped yet)
  if (status === "idle" || status === "error") {
    const hasResume = savedProgress && savedProgress.position > 3;
    return (
      <div
        className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 flex items-center gap-4"
        data-testid="audio-idle"
      >
        <button
          onClick={generate}
          data-testid="audio-listen-btn"
          className="w-11 h-11 rounded-full bg-[var(--accent)] text-white flex items-center justify-center hover:bg-[var(--accent)]/90 transition-transform hover:scale-105 flex-shrink-0"
          aria-label={hasResume ? T("Resume", "Continuar") : T("Listen", "Escuchar")}
        >
          {hasResume ? (
            <Play className="w-5 h-5 ml-0.5" strokeWidth={1.75} />
          ) : (
            <Headphones className="w-5 h-5" strokeWidth={1.75} />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
            {T("Audio Bible", "Biblia en audio")}
          </p>
          <p className="text-sm text-[var(--text-primary)] mt-0.5 font-serif">
            {hasResume
              ? T(`Continue from ${fmt(savedProgress.position)}`, `Continuar desde ${fmt(savedProgress.position)}`)
              : T("Listen to this chapter", "Escuchar este capítulo")}
          </p>
        </div>
        {hasResume && (
          <button
            onClick={() => {
              clearProgress(translation, book, chapter);
              setSavedProgress(null);
              resumePendingRef.current = null;
            }}
            data-testid="audio-restart-btn"
            className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
          >
            {T("Start over", "Reiniciar")}
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (status === "loading") {
    return (
      <div
        className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 flex items-center gap-4"
        data-testid="audio-loading"
      >
        <div className="w-11 h-11 rounded-full bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
          <Loader2 className="w-5 h-5 text-[var(--accent)] animate-spin" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
            {longChapter
              ? T("Still preparing (long chapter)…", "Aún preparando (capítulo largo)…")
              : T("Preparing audio…", "Preparando audio…")}
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {longChapter
              ? T("Long chapters can take 1–2 minutes the first time. Hold on.", "Los capítulos largos pueden tardar 1–2 minutos la primera vez. Espera un momento.")
              : T("~10–25 s the first time on a chapter. Next time it's instant.", "~10–25 s la primera vez en un capítulo. La próxima vez es instantáneo.")}
          </p>
        </div>
      </div>
    );
  }

  // Ready / playing / paused state
  const pct = duration > 0 ? (current / duration) * 100 : 0;
  return (
    <div
      className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4"
      data-testid="audio-player"
    >
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="auto"
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration || 0);
          // Try seeking here first — most browsers accept it once metadata is ready.
          const resume = resumePendingRef.current;
          const d = e.currentTarget.duration || 0;
          if (resume && resume > 3 && d && resume < d - FINISHED_MARGIN_SECONDS) {
            try {
              e.currentTarget.currentTime = resume;
            } catch (_err) {
              // ignore, retry in onCanPlay
            }
          }
        }}
        onCanPlay={(e) => {
          // Second attempt in case the seek in onLoadedMetadata was rejected because
          // the buffered range didn't yet include the resume position.
          const resume = resumePendingRef.current;
          const d = e.currentTarget.duration || 0;
          if (resume && resume > 3 && d && resume < d - FINISHED_MARGIN_SECONDS) {
            try {
              e.currentTarget.currentTime = resume;
            } catch (_err) {
              // ignore
            }
          }
          resumePendingRef.current = null;
          // Kick off playback once the source can play (browsers may throttle autoplay
          // on cold loads, so guard with a catch).
          if (status === "ready") {
            e.currentTarget.play().catch(() => {});
          }
        }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime || 0;
          setCurrent(t);
          // Throttle writes to ~1 per 4 seconds
          const now = Date.now();
          if (now - lastSaveRef.current >= 4000 && t > 1 && e.currentTarget.duration) {
            lastSaveRef.current = now;
            saveProgress(translation, book, chapter, t, e.currentTarget.duration);
          }
        }}
        onPlay={() => setStatus("playing")}
        onPause={(e) => {
          setStatus("paused");
          const t = e.currentTarget.currentTime || 0;
          const d = e.currentTarget.duration || 0;
          if (d && t < d - FINISHED_MARGIN_SECONDS && t > 1) {
            saveProgress(translation, book, chapter, t, d);
          }
        }}
        onEnded={() => {
          setStatus("paused");
          clearProgress(translation, book, chapter);
          setSavedProgress(null);
        }}
      />

      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          data-testid="audio-play-toggle"
          className="w-11 h-11 rounded-full bg-[var(--accent)] text-white flex items-center justify-center hover:bg-[var(--accent)]/90 transition-transform hover:scale-105 flex-shrink-0"
          aria-label={status === "playing" ? "Pause" : "Play"}
        >
          {status === "playing" ? (
            <Pause className="w-5 h-5" strokeWidth={2} />
          ) : (
            <Play className="w-5 h-5 ml-0.5" strokeWidth={2} />
          )}
        </button>

        <button
          onClick={() => seek(-15)}
          data-testid="audio-back-15"
          className="w-9 h-9 rounded-full border border-[var(--border)] text-[var(--text-primary)] flex items-center justify-center hover:bg-[var(--surface)] transition-colors flex-shrink-0"
          aria-label="Back 15 seconds"
        >
          <Rewind className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>

        <button
          onClick={() => seek(15)}
          data-testid="audio-fwd-15"
          className="w-9 h-9 rounded-full border border-[var(--border)] text-[var(--text-primary)] flex items-center justify-center hover:bg-[var(--surface)] transition-colors flex-shrink-0"
          aria-label="Forward 15 seconds"
        >
          <FastForward className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>

        <button
          onClick={changeSpeed}
          data-testid="audio-speed"
          className="ml-auto px-3 h-8 rounded-full border border-[var(--border)] text-[10px] uppercase tracking-widest text-[var(--text-secondary)] hover:bg-[var(--surface)] transition-colors flex-shrink-0"
        >
          {speed}×
        </button>
      </div>

      <div className="mt-3">
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={pct}
          onChange={onScrub}
          data-testid="audio-scrub"
          className="w-full h-1 bg-[var(--surface)] rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
          aria-label="Seek"
        />
        <div className="flex justify-between mt-1.5 text-[10px] uppercase tracking-widest text-[var(--text-secondary)] font-mono">
          <span>{fmt(current)}</span>
          <span>
            {native ? T("· native latin voice", "· voz nativa latina") : (dramatized ? T("· dramatized", "· dramatizado") : (voice ? voice.toUpperCase() : ""))}
            {cacheHit === false && <span className="ml-2 text-[var(--accent)]">{T("· fresh", "· recién generado")}</span>}
          </span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
    </div>
  );
}
